import React, { useState, useEffect } from 'react';
import {
  CountriesList,
  fetchCountriesList,
  AccountGroup,
} from '@amzn/aac-chrome-internal-api';
import { createContext, useContext } from 'react';
import { RegionSwitcherPublisher } from 'src/commons/utils/metrics';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { logger } from 'src/commons/utils/logger';
import { useAccountDropdownContext } from '../AccountDropdown/AccountDropdownProvider';
import { Region } from '@amzn/d16-g-adsp-region-switcher-ui/dist/src/types/Region';

export type RegionSwitcherAccount = {
  entityId: string;
  url: string;
  regionId: Region;
  cfid?: string;
  marketplaceId: string;
};

interface RegionSwitcherContextValue {
  accounts: RegionSwitcherAccount[];
  currentAccount: RegionSwitcherAccount | null;
}

export const RegionSwitcherContext = createContext<RegionSwitcherContextValue>({
  accounts: [],
  currentAccount: null,
});

export const useRegionSwitcherContext = () => {
  const context = useContext(RegionSwitcherContext);
  if (context === undefined) {
    throw new Error(
      'useRegionSwitcherContext must be used within a RegionSwitcherProvider',
    );
  }
  return context;
};

const findCurrentAccountsCountriesController = (
  countryListAccounts: CountriesList,
  currentMarketplaceId: string,
) => {
  const accounts = transformRegionSwitcherAccounts(countryListAccounts);
  let currentAccount;
  let regionalAccounts;
  for (const account of accounts) {
    if (account.marketplaceId === currentMarketplaceId) {
      currentAccount = account;
      regionalAccounts = accounts;
      break;
    }
  }
  return [currentAccount, regionalAccounts];
};

// TODO: Deprecate when the weblab is fully running
const findCurrentAccountNavStateController = (
  currentEntityId: string,
  currentMarketplaceId: string,
  accounts: AccountGroup[],
) => {
  let currentAccount;
  let regionalAccounts;

  for (const account of accounts) {
    for (const subAccount of account.accounts) {
      if (subAccount.entityId === currentEntityId) {
        if (subAccount.nestedAccounts) {
          regionalAccounts = subAccount.nestedAccounts;
          for (const nestedAccount of subAccount.nestedAccounts) {
            if (nestedAccount.marketplaceId === currentMarketplaceId) {
              currentAccount = nestedAccount;
              break;
            }
          }
          break;
        }
      }
    }
  }
  return [currentAccount, regionalAccounts];
};

const determineAccounts = (
  countryListAccounts: CountriesList,
  currentMarketplaceId: string,
  currentEntityId: string,
  navStateAccounts: AccountGroup[],
  isRegionSwitcherUsingGetCountries: boolean,
) =>
  isRegionSwitcherUsingGetCountries
    ? findCurrentAccountsCountriesController(
        countryListAccounts,
        currentMarketplaceId,
      )
    : findCurrentAccountNavStateController(
        currentEntityId,
        currentMarketplaceId,
        navStateAccounts,
      );

const transformRegionSwitcherAccounts = (
  regionSwitcherAccounts: CountriesList,
): RegionSwitcherAccount[] => {
  const accounts: RegionSwitcherAccount[] = [];
  Object.keys(regionSwitcherAccounts).forEach((key) => {
    const { url, regionId, marketplaceId, cfid } = regionSwitcherAccounts[key];
    // If url or regionId are undefined then object should not be passed to the regionSwitcherSelector
    if (url && regionId) {
      accounts.push({
        entityId: key,
        url,
        regionId: regionId as Region,
        cfid,
        marketplaceId,
      });
    }
  });
  return accounts;
};

export const RegionSwitcherProvider: React.FC = ({ children }) => {
  const [accounts, setAccounts] = useState<CountriesList>({});
  const isRegionSwitcherUsingGetCountries = useIsFeatureEnabled(
    WeblabedFeatures.CONTEXT_SERVICE_ADSP_GLOBAL_ACCOUNT,
  );
  const accountDropdownContext = useAccountDropdownContext();
  const navStateAccounts = accountDropdownContext.accounts;
  const { currentEntityId, currentMarketplaceId } = accountDropdownContext;
  const [currentAccount, regionalAccounts] = determineAccounts(
    accounts,
    currentMarketplaceId,
    currentEntityId,
    navStateAccounts,
    isRegionSwitcherUsingGetCountries,
  );

  // Ajax load region switcher state from regionSwitcherState api
  const fetchRegionSwitcherStateInfo = async () => {
    try {
      // Checks is /advertisers/{cfid}/ is in the url and pulls out the cfid
      const urlMatch = window.location.pathname.match(/advertisers\/([^/]+)/);
      const cfid = urlMatch ? urlMatch[1] : undefined;
      const regionSwitcherState = await fetchCountriesList({}, cfid);
      setAccounts(regionSwitcherState);
      RegionSwitcherPublisher.publishCounterMonitor(
        'regionSwitcherFailure-fetchRegionSwitcherStateInfo',
        0,
      );
    } catch (e: any) {
      RegionSwitcherPublisher.publishCounterMonitor(
        'regionSwitcherFailure-fetchRegionSwitcherStateInfo',
        1,
      );
      logger.error('fetchRegionSwitcherStateInfo failed', e);
    }
  };

  useEffect(() => {
    if (isRegionSwitcherUsingGetCountries) fetchRegionSwitcherStateInfo();
  }, [isRegionSwitcherUsingGetCountries]);

  return (
    <RegionSwitcherContext.Provider
      value={{ accounts: regionalAccounts, currentAccount }}
    >
      {children}
    </RegionSwitcherContext.Provider>
  );
};
